import { useEffect, useState, memo } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db, normalizeProduct } from '@/lib/firebase';
import ProductImage from '@/components/ProductImage';

const catColors = [
  'from-rose-100 to-rose-200/30',
  'from-sky-100 to-sky-200/30',
  'from-amber-100 to-amber-200/30',
  'from-emerald-100 to-emerald-200/30',
  'from-violet-100 to-violet-200/30',
  'from-cyan-100 to-cyan-200/30',
];

// OPTİMİZASYON: Animasyonlar için donanım hızlandırmalı variantlar
const revealVariants = {
  hidden: { y: "105%", opacity: 0 },
  visible: { 
    y: 0, 
    opacity: 1,
    transition: { duration: 0.8, ease: [0.76, 0, 0.24, 1] } 
  }
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.2 } 
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { duration: 0.7, ease: [0.76, 0, 0.24, 1] } 
  }
};

// OPTİMİZASYON: Memo kullanarak gereksiz re-render'ları engelliyoruz
const Categories = memo(() => {
  const [categories, setCategories] = useState([]);
  const [categoryImages, setCategoryImages] = useState({});

  useEffect(() => {
    let isMounted = true;
    const fetchCategoriesData = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'urunler'));
        if (!isMounted) return;
        
        const products = snapshot.docs.map((doc) => normalizeProduct(doc));
        const catMap = {};
        products.forEach((p) => {
          const cat = p.category || 'Diğer';
          if (!catMap[cat]) catMap[cat] = { name: cat, count: 0 };
          catMap[cat].count++;
        });
        
        const cats = Object.values(catMap).sort((a, b) => b.count - a.count);
        setCategories(cats);

        const catDoc = await getDoc(doc(db, 'site_config', 'categories'));
        if (isMounted && catDoc.exists() && catDoc.data().images) {
          setCategoryImages(catDoc.data().images);
        }
      } catch (err) { console.error('Categories error:', err); }
    };
    fetchCategoriesData();
    return () => { isMounted = false; };
  }, []);

  if (categories.length === 0) return null;

  return (
    <section className="py-20 md:py-32 bg-brand-cream/30 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        
        <div className="mb-16 md:mb-20">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
          >
            <div className="overflow-hidden py-1">
              <motion.span 
                variants={revealVariants}
                className="inline-block text-[10px] md:text-xs font-bold uppercase tracking-[0.3em] text-brand-dark/40 mb-3"
              >
                Koleksiyonlar
              </motion.span>
            </div>
            <div className="overflow-hidden py-1">
              <motion.h2 
                variants={revealVariants}
                className="text-4xl md:text-6xl font-extrabold text-brand-dark tracking-tighter"
              >
                Ürün Dünyamız
              </motion.h2>
            </div>
          </motion.div>
        </div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8"
        >
          {categories.map((cat, i) => {
            const hasImage = categoryImages[cat.name];
            
            return (
              <motion.div 
                key={cat.name} 
                variants={itemVariants}
                style={{ willChange: "transform, opacity" }} // OPTİMİZASYON: GPU'ya ön bildirim
              >
                <Link
                  to={`/urunler?kategori=${encodeURIComponent(cat.name)}`}
                  className="group relative block aspect-[4/5] overflow-hidden rounded-[2rem] bg-white shadow-sm transition-all duration-500 hover:shadow-2xl hover:shadow-brand-dark/10"
                >
                  <div className={`absolute inset-0 transition-transform duration-700 group-hover:scale-110 ${!hasImage ? `bg-gradient-to-br ${catColors[i % catColors.length]}` : ''}`}>
                    {hasImage && (
                      <>
                        <ProductImage 
                          src={hasImage} 
                          alt={cat.name} 
                          className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110 group-hover:rotate-1"
                          loading="lazy" // OPTİMİZASYON: Tarayıcıyı rahatlatır
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/80 via-brand-dark/20 to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-500" />
                      </>
                    )}
                  </div>

                  <div className="absolute top-6 left-6 z-20">
                    <div className="flex items-center justify-center px-3 py-1 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-[10px] font-bold text-white uppercase tracking-widest">
                      {cat.count} ÜRÜN
                    </div>
                  </div>

                  <div className="absolute bottom-8 left-8 right-8 z-20">
                    <div className="overflow-hidden">
                      <h3 className={`text-2xl font-bold tracking-tight mb-1 transition-transform duration-500 group-hover:-translate-y-1 ${hasImage ? 'text-white' : 'text-brand-dark'}`}>
                        {cat.name}
                      </h3>
                    </div>
                    <div className="flex items-center gap-2 opacity-0 -translate-x-4 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-500 delay-100">
                      <span className={`text-[10px] font-bold uppercase tracking-widest ${hasImage ? 'text-white/60' : 'text-brand-dark/60'}`}>Keşfet</span>
                      <div className={`w-8 h-[1px] ${hasImage ? 'bg-white/40' : 'bg-brand-dark/20'}`} />
                    </div>
                  </div>

                  {/* OPTİMİZASYON: Layer ayrımı için translateZ */}
                  <div 
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 bg-gradient-to-tr from-white/5 to-transparent pointer-events-none"
                    style={{ transform: "translateZ(0)" }}
                  />
                </Link>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
});

export default Categories;